import { useEffect, useRef } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ListChecks, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChatComposer } from "@/components/looseend/ChatComposer";
import { EmptyState } from "@/components/looseend/EmptyState";
import { EmptyConversationState } from "@/components/looseend/EmptyConversationState";
import { MessageBubble } from "@/components/looseend/MessageBubble";
import { InfinityMark } from "@/components/looseend/Wordmark";
import { useInspector } from "@/components/looseend/inspector-context";
import { useHostedChatActions, useHostedTranscript } from "@/lib/chat/product/hostedChat";
import { useAppState } from "@/lib/looseend/store";
import { analytics } from "@/lib/analytics";
import type { LoopPreviewDraft, Message, MessageAttachment } from "@/lib/looseend/types";

export const Route = createFileRoute("/app/chat/$conversationId")({
  head: () => ({
    meta: [
      { title: "Conversation — L∞seEnd" },
      {
        name: "description",
        content: "Describe an unresolved commitment and L∞seEnd compiles it into a Loop.",
      },
      { property: "og:title", content: "Conversation — L∞seEnd" },
      { property: "og:description", content: "Capture loose ends in plain language." },
    ],
  }),
  component: ChatWorkspace,
});

/**
 * WP-B — The conversation surface renders exactly one authoritative transcript
 * read. Nothing is written locally: a turn is a server command, and what appears
 * afterwards is what the backend stored.
 */
function ChatWorkspace() {
  const { conversationId } = Route.useParams();
  const navigate = useNavigate();
  const isGuest = useAppState().user?.isGuest === true;
  const transcript = useHostedTranscript(conversationId);
  const chat = useHostedChatActions();
  const { open } = useInspector();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [transcript.messages.length, chat.busy]);

  const send = (text: string, _attachments?: MessageAttachment[]) => {
    void _attachments;
    analytics.track("loop_capture_started", { interface_surface: "conversation" });
    void chat.sendTurn(conversationId, text).then((result) => {
      if (!result.ok) {
        toast.error(result.message);
        return;
      }
      analytics.track("loop_preview_generated", {
        interface_surface: "conversation",
        success: true,
      });
    });
  };

  /**
   * WP-C1 — Saving a suggestion is proposal canonicalization.
   *
   * The card's draft is user content. Any edits are persisted to the DURABLE
   * proposal first; then the durable proposal — identified by the id and version
   * the hosted read returned, never by a browser-built Loop payload — is
   * canonicalized. The backend creates the Loop, confirms the proposal and links
   * the conversation in one transaction, and the transcript is re-read.
   *
   * Guest-first access: a guest may converse and read the proposal, but a Loop
   * is durable account-owned state. Identity is asked for HERE, when it becomes
   * valuable — the server refuses anonymous canonicalization regardless.
   */
  const saveLoop = async (reviewed: LoopPreviewDraft, message: Message) => {
    if (isGuest) {
      // Pre-canonical: describe the user's intent, never a Loop that doesn't exist yet.
      toast.info("Sign in to keep track of this.", {
        description: "Your conversation comes with you.",
        action: {
          label: "Sign in",
          onClick: () =>
            void navigate({
              to: "/auth",
              search: { redirect: `/app/chat/${conversationId}` },
            }),
        },
      });
      return;
    }
    if (!message.proposal || !message.loopPreview) {
      toast.error("This suggestion is no longer available.");
      return;
    }
    const result = await chat.saveProposal({
      conversationId,
      proposalId: message.proposal.id,
      expectedVersion: message.proposal.recordVersion,
      stored: message.loopPreview,
      reviewed,
    });
    if (!result.ok) {
      toast.error(result.message);
      return;
    }
    analytics.track("loop_saved", { interface_surface: "conversation", success: true });
    open(result.value.loopId);
    // Experience → label → understanding: the word "Loop" is introduced here,
    // after the user has confirmed what is unresolved.
    if (result.value.disposition === "EXISTING") {
      toast.success("This is already being tracked as a Loop.", {
        description: "Nothing new was created. LooseEnd will surface the next useful action.",
      });
    } else {
      toast.success("Got it — this is now a Loop.", {
        description:
          "A Loop is something unresolved that LooseEnd keeps track of until it's resolved. PEE™ will surface the next useful action.",
      });
    }
  };

  if (transcript.phase === "loading") {
    return (
      <div className="flex h-full flex-col gap-3 p-6" aria-busy="true">
        <div className="h-4 w-32 animate-pulse rounded bg-muted" />
        <div className="h-20 w-2/3 animate-pulse rounded-xl bg-muted" />
        <div className="h-20 w-1/2 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (transcript.phase !== "ready") {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <EmptyState
          icon={<MessageSquarePlus className="size-6" aria-hidden="true" />}
          title={
            transcript.phase === "not_found"
              ? "This conversation is no longer available"
              : "Conversations are briefly unavailable"
          }
          description={transcript.message ?? ""}
          action={
            transcript.phase === "not_found" ? (
              <Button asChild>
                <Link to="/app">Start a new conversation</Link>
              </Button>
            ) : (
              <Button variant="outline" onClick={transcript.refresh}>
                Try again
              </Button>
            )
          }
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="hidden shrink-0 items-center gap-3 border-b border-border px-6 py-3.5 lg:flex">
        <h1 className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {transcript.title}
        </h1>
        <Button variant="outline" size="sm" asChild>
          <Link to="/app/queue">
            <ListChecks className="size-4" aria-hidden="true" />
            Resolution Queue
          </Link>
        </Button>
      </header>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
        {transcript.messages.length === 0 ? (
          <EmptyConversationState onSuggestion={send} />
        ) : (
          <div className="space-y-7">
            {transcript.messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                savingLoop={chat.busy}
                onSaveLoop={saveLoop}
                onOpenLoop={open}
              />
            ))}
            {chat.busy ? (
              <div className="content-column flex items-center gap-3 text-sm text-muted-foreground">
                <span className="grid size-7 shrink-0 place-items-center rounded-full border border-border bg-card">
                  <InfinityMark className="size-4 animate-pulse text-primary" />
                </span>
                LooseEnd is figuring out what needs to happen next…
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="shrink-0 bg-background px-4 pt-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-6">
        <ChatComposer
          onSend={send}
          disabled={chat.busy}
          // Presentation only: the hosted transcript already says whether an
          // exchange exists. A non-empty transcript means the user is replying.
          placeholder={
            transcript.messages.length > 0
              ? "Reply to LooseEnd…"
              : "Tell LooseEnd what's unresolved…"
          }
        />
      </div>
    </div>
  );
}
